import { ArrowRight, FileText, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { navigateToViewByType } from "@/other/utils/asyncTaskUtils.jsx";
import { MANIFEST_GUIDE_TARGETS, useAutoPageGuide, guideTargetProps } from "@/react/guides";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
  useActiveBoardContext,
} from "@/react/components/marketingStack";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import {
  SMMGeneratorProvider,
  useSMMGeneratorContext,
} from "./context/SMMGeneratorContext";
import { TagReviewDialog } from "./components/TagReviewDialog";
import { ManifestEditor } from "./components/ManifestEditor";
import { ManifestProvenanceBar } from "./components/ManifestProvenanceBar";
import { ManifestTagsNavButton } from "./components/ManifestTagsNavButton";
import { useManifestMarketingTags } from "./utils/useManifestMarketingTags";

const ManifestEmptyState = ({ onGoToContext }: { onGoToContext: () => void }) => (
  <div
    className="flex flex-1 min-h-0 rounded-lg border border-dashed border-border bg-card/40 overflow-hidden"
    {...guideTargetProps(MANIFEST_GUIDE_TARGETS.emptyState)}
  >
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-8 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-muted/80 text-muted-foreground">
        <FileText className="h-5 w-5" />
      </div>
      <h3 className="text-base font-semibold text-foreground">Your manifest will appear here</h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
        Once you set up Context and make it active, Swivu compiles a marketing blueprint and generates tags automatically.
      </p>
      <Button onClick={onGoToContext} className="mt-5 gap-1.5">
        Set up Context
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  </div>
);

const MarketingManifestContent = () => {
  const { viewManager } = useSwivuBryntumContext();
  const { activeContext } = useActiveBoardContext();
  const [isManifestFocused, setIsManifestFocused] = useState(false);
  const {
    manifestContent,
    isLoading,
    isGenerating,
    isGeneratingTags,
    setTagReviewOpen,
  } = useSMMGeneratorContext();
  const { tagCount } = useManifestMarketingTags();

  const goToContext = useCallback(() => {
    if (!viewManager) return;
    navigateToViewByType(
      viewManager,
      VIEW_TYPES.context,
      "Context",
      activeContext?.id != null ? { contextId: activeContext.id } : {},
    );
  }, [viewManager, activeContext?.id]);

  const toggleManifestFocus = useCallback(() => {
    setIsManifestFocused((prev) => !prev);
  }, []);

  const hasManifest = Boolean(manifestContent) || isGenerating;

  const { replayGuide } = useAutoPageGuide({
    viewType: VIEW_TYPES.marketingManifest,
    enabled: !isLoading,
    contextKey: hasManifest ? "ready" : "empty",
  });

  useEffect(() => {
    if (!isManifestFocused) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsManifestFocused(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isManifestFocused]);

  useEffect(() => {
    if (!hasManifest && isManifestFocused) {
      setIsManifestFocused(false);
    }
  }, [hasManifest, isManifestFocused]);

  const workflowVariant = !hasManifest ? "empty" : isGeneratingTags ? "generating-tags" : "ready";

  const manifestStepOverrides = useMemo(
    () => ({
      [VIEW_TYPES.context]: {
        props: guideTargetProps(MANIFEST_GUIDE_TARGETS.context),
        onClick: goToContext,
      },
      [VIEW_TYPES.marketingManifest]: {
        onShowGuide: !isManifestFocused ? replayGuide : undefined,
      },
    }),
    [goToContext, isManifestFocused, replayGuide],
  );

  const manifestNavAfterStep = useMemo(() => {
    if (!hasManifest) return undefined;

    return {
      [VIEW_TYPES.marketingManifest]: (
        <ManifestTagsNavButton
          variant={workflowVariant}
          tagCount={tagCount}
          onOpen={() => setTagReviewOpen(true)}
        />
      ),
    };
  }, [hasManifest, workflowVariant, tagCount, setTagReviewOpen]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={cn("relative", marketingStackPageClassName)}>
      {!isManifestFocused && (
        <MarketingStackWorkflowStrip
          activeView={VIEW_TYPES.marketingManifest}
          stepOverrides={manifestStepOverrides}
          navAfterStep={manifestNavAfterStep}
          hint={
            workflowVariant === "empty"
              ? "Set up Context first — your manifest and tags are generated automatically when you activate it."
              : undefined
          }
        />
      )}

      <div
        className={cn(
          "flex flex-1 min-h-0 flex-col overflow-hidden",
          !isManifestFocused && marketingStackContentInsetClassName,
          isManifestFocused && "absolute inset-0 z-30 bg-background",
        )}
      >
        {hasManifest ? (
          <>
            <div
              className={cn(
                marketingStackCardClassName("bg-card"),
                isManifestFocused && "rounded-none border-0",
              )}
            >
              <ManifestEditor
                onGoToContext={goToContext}
                onShowGuide={isManifestFocused ? replayGuide : undefined}
                isFocused={isManifestFocused}
                onToggleFocus={toggleManifestFocus}
              />
              {!isManifestFocused && <ManifestProvenanceBar isGenerating={isGenerating} />}
            </div>
          </>
        ) : (
          <>
            <ManifestEmptyState onGoToContext={goToContext} />
            <div className="mt-1.5 overflow-hidden rounded-lg border border-border">
              <ManifestProvenanceBar isGenerating={isGenerating} showTopBorder={false} />
            </div>
          </>
        )}
      </div>
      {hasManifest && <TagReviewDialog />}
    </div>
  );
};

const MarketingManifestView = () => (
  <SMMGeneratorProvider>
    <div className="h-full min-h-0">
      <MarketingManifestContent />
    </div>
  </SMMGeneratorProvider>
);

export default MarketingManifestView;
